import { action, makeAutoObservable } from "mobx";
import CommonStore from "../../../stores/CommonStore";
import { ProductOrderService } from "../../../service/productOrder/ProductOrderService";
import ProductOrderAttachmentType from "../../../model/productOrder/ProductOrderAttachmentType";

interface UploadImageRequest {
  base64File: string;
  description?: string;
  type: ProductOrderAttachmentType;
}

class NewAttachmentButtonStore {
  public productOrderId: string;
  public isModalOpen: boolean;
  public loading: boolean;
  public commonStore: CommonStore;

  // Estado da imagem selecionada
  public selectedImageBase64: string = '';
  public selectedImagePreview: string = '';
  public imageDescription: string = '';
  public imageType: ProductOrderAttachmentType = ProductOrderAttachmentType.DELIVERY_EVIDENCE;
  public isUploading: boolean = false;

  constructor(commonStore: CommonStore, productOrderId: string) {
    this.productOrderId = productOrderId;
    this.isModalOpen = false;
    this.loading = false;
    this.commonStore = commonStore;
    makeAutoObservable(this);
  }

  /**
   * Verifica se o botão de salvar deve estar habilitado
   */
  get enableButton(): boolean {
    return !!this.selectedImageBase64 && !this.isUploading;
  }

  /**
   * Abre o modal
   */
  @action
  public async openModal() {
    this.isModalOpen = true;
    this.resetImageState();
  }

  /**
   * Fecha o modal
   */
  @action
  public closeModal() {
    this.isModalOpen = false;
    this.resetImageState();
  }

  /**
   * Reseta o estado da imagem
   */
  @action
  private resetImageState() {
    this.selectedImageBase64 = '';
    this.selectedImagePreview = '';
    this.imageDescription = '';
    this.imageType = ProductOrderAttachmentType.DELIVERY_EVIDENCE;
    this.isUploading = false;
  }

  /**
   * Processa a imagem selecionada pelo usuário
   */
  @action
  public async processSelectedImage(file: File): Promise<boolean> {
    try {
      this.loading = true;

      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        // this.commonStore.showError('Apenas imagens são permitidas');
        this.loading = false;
        return false;
      }

      // Validar tamanho (máximo 3MB)
      const sizeInMB = file.size / (1024 * 1024);
      if (sizeInMB > 3) {
        // this.commonStore.showError('Imagem muito grande. Tamanho máximo: 3MB');
        this.loading = false;
        return false;
      }

      // Converter para base64
      const base64String = await this.fileToBase64(file);
      
      // Comprimir a imagem se necessário
      const compressedBase64 = await this.compressImage(base64String, file.type);

      // Armazenar a imagem
      this.selectedImageBase64 = compressedBase64;
      this.selectedImagePreview = compressedBase64;
      this.imageDescription = '';

      this.loading = false;
      return true;
    } catch (error) {
      console.error('Erro ao processar imagem:', error);
      // this.commonStore.showError('Erro ao processar imagem');
      this.loading = false;
      return false;
    }
  }

  /**
   * Converte arquivo para base64
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Comprime a imagem
   */
  private compressImage(base64String: string, mimeType: string): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Redimensionar se necessário (máximo 1920x1440)
        const maxWidth = 1920;
        const maxHeight = 1440;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
        }

        // Converter para base64 com qualidade reduzida
        const compressedBase64 = canvas.toDataURL(mimeType, 0.8);
        resolve(compressedBase64);
      };
      img.src = base64String;
    });
  }

  /**
   * Atualiza a descrição da imagem
   */
  @action
  public setImageDescription(description: string) {
    this.imageDescription = description;
  }

  /**
   * Atualiza o tipo da imagem
   */
  @action
  public setImageType(type: ProductOrderAttachmentType) {
    this.imageType = type;
  }

  /**
   * Faz upload da imagem
   */
  @action
  public async save(): Promise<boolean> {
    try {
      if (!this.selectedImageBase64) {
        // this.commonStore.showError('Nenhuma imagem selecionada');
        return false;
      }

      this.isUploading = true;

      const uploadRequest: UploadImageRequest = {
        base64File: this.selectedImageBase64,
        description: this.imageDescription || undefined,
        type: this.imageType,
      };

      const response = await ProductOrderService.uploadAttachment(
        this.productOrderId,
        uploadRequest
      );

      if (response.success) {
        // this.commonStore.showSuccess('Imagem enviada com sucesso!');
        this.resetImageState();
        this.isUploading = false;
        return true;
      } else {
        this.commonStore.processErrors(response);
        this.isUploading = false;
        return false;
      }
    } catch (error) {
      console.error('Erro ao enviar imagem:', error);
      // this.commonStore.showError('Erro ao enviar imagem');
      this.isUploading = false;
      return false;
    }
  }
}

export default NewAttachmentButtonStore;
